import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Container,
  Group,
  Loader,
  Progress,
  RingProgress,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconPlayerPlay,
  IconPlayerStop,
  IconRefresh,
  IconSearch,
  IconExternalLink,
  IconX,
} from '@tabler/icons-react';
import useAuthenticatedData from '../../hooks/useAuthenticatedData';
import useAuthenticatedPost from '../../hooks/useAuthenticatedPost';
import config from '../../config';

// Query Commander health dashboard.
//
// Validation is CLIENT-DRIVEN: the browser loads the full query list (no execution), then calls
// POST /queries/validate/{key} one query at a time with a small concurrency pool, updating each row
// as it resolves. This replaced a single POST /queries/validate-all that ran all ~295 queries
// serially inside one request — it blocked for minutes with zero feedback and its results only
// landed at the very end. Now every query is visible: pending → running → ok/warn/error, with an
// overall X/Y counter, progress bar and live ETA, and a Cancel that actually stops the run.
//
// After a full (non-cancelled) pass we hit POST /queries/refresh-tree-errors so the explorer tree's
// red X's catch up to the freshly persisted per-query HasError flags.

const CONCURRENCY = 4;

// The API serializes QueryValidationStatus as a NUMBER (0=Ok,1=Warning,2=Error) — no
// JsonStringEnumConverter is registered. Accept both number and string forms defensively.
const STATUS_META = {
  ok: { color: 'teal', label: 'OK' },
  warning: { color: 'yellow', label: 'Warning' },
  error: { color: 'red', label: 'Error' },
  empty: { color: 'gray', label: 'No SQL' },
  running: { color: 'blue', label: 'Running' },
  unchecked: { color: 'gray', label: 'Not run' },
};

function normStatus(s) {
  if (typeof s === 'number') return ['ok', 'warning', 'error', 'empty'][s] || 'warning';
  return String(s ?? '').toLowerCase();
}

function StatusCell({ state }) {
  const status = state?.status ?? 'unchecked';
  if (status === 'running') {
    return (
      <Badge color="blue" variant="light" size="sm" leftSection={<Loader size={10} color="blue" />}>
        Running
      </Badge>
    );
  }
  const m = STATUS_META[status] ?? STATUS_META.unchecked;
  return (
    <Badge color={m.color} variant={status === 'unchecked' ? 'outline' : 'filled'} size="sm">
      {m.label}
    </Badge>
  );
}

function problemText(report) {
  if (!report) return '';
  const status = normStatus(report.status);
  if (status === 'ok') return report.message || 'Healthy';
  if (status === 'empty') return report.message || 'No SQL defined (empty stub).';
  const parts = [];
  // Surface the real reason it failed, not just "does not execute".
  if (report.executes === false) parts.push(report.message || 'does not execute');
  if (report.missingStoredColumns?.length) {
    parts.push(
      `${report.missingStoredColumns.length} registered column(s) no longer returned: ${report.missingStoredColumns.join(', ')}`
    );
  }
  if (report.unmappedResultColumns?.length) {
    parts.push(`${report.unmappedResultColumns.length} result column(s) not registered`);
  }
  if (report.argIssues?.length) parts.push(report.argIssues.join('; '));
  return parts.join(' · ') || report.message || '';
}

function fmtEta(ms) {
  if (!ms || ms <= 0 || !isFinite(ms)) return '—';
  const s = Math.ceil(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}m ${String(r).padStart(2, '0')}s` : `${r}s`;
}

export default function QueryHealth() {
  const { post } = useAuthenticatedPost();
  const { data: keyList, isLoading: listLoading, error: listError, refetch } = useAuthenticatedData(
    `${config.apiBaseUrl}/queries/keys`
  );

  // queryKey -> { status, report }
  const [results, setResults] = useState({});
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [etaMs, setEtaMs] = useState(0);
  const [runError, setRunError] = useState(null);
  const [search, setSearch] = useState('');
  const [resyncing, setResyncing] = useState({});

  const cancelledRef = useRef(false);

  const keys = useMemo(() => keyList ?? [], [keyList]);

  // Reset per-query state whenever a fresh list arrives.
  useEffect(() => {
    setResults({});
    setProgress({ done: 0, total: keys.length });
  }, [keys]);

  const runAll = async () => {
    if (running || !keys.length) return;
    cancelledRef.current = false;
    setRunError(null);
    setResults({});
    setProgress({ done: 0, total: keys.length });
    setRunning(true);

    const list = keys.map((k) => k.queryKey);
    const start = performance.now();
    let done = 0;
    let idx = 0;

    const worker = async () => {
      // Pull the next index until the list is exhausted or the user cancels.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (cancelledRef.current) return;
        const myIdx = idx;
        idx += 1;
        if (myIdx >= list.length) return;
        const key = list[myIdx];

        setResults((r) => ({ ...r, [key]: { status: 'running' } }));
        try {
          const report = await post(
            `${config.apiBaseUrl}/queries/validate/${encodeURIComponent(key)}`,
            {}
          );
          setResults((r) => ({ ...r, [key]: { status: normStatus(report.status), report } }));
        } catch (e) {
          setResults((r) => ({
            ...r,
            [key]: {
              status: 'error',
              report: { queryKey: key, status: 2, executes: false, message: e?.message || 'request failed' },
            },
          }));
        } finally {
          done += 1;
          setProgress({ done, total: list.length });
          const elapsed = performance.now() - start;
          const remaining = list.length - done;
          setEtaMs(done > 0 ? (elapsed / done) * remaining : 0);
        }
      }
    };

    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, list.length) }, worker));
    setRunning(false);
    setEtaMs(0);

    if (!cancelledRef.current) {
      // Bring the explorer tree's error propagation in line with the freshly persisted flags.
      try {
        await post(`${config.apiBaseUrl}/queries/refresh-tree-errors`, {});
      } catch (e) {
        setRunError(`Validation finished, but the tree error refresh failed: ${e?.message || 'error'}`);
      }
    }
  };

  const cancel = () => {
    cancelledRef.current = true;
  };

  const resyncOne = async (queryKey) => {
    setResyncing((s) => ({ ...s, [queryKey]: true }));
    try {
      await post(`${config.apiBaseUrl}/queries/query/${encodeURIComponent(queryKey)}/auto-populate`, {});
      const report = await post(`${config.apiBaseUrl}/queries/validate/${encodeURIComponent(queryKey)}`, {});
      setResults((r) => ({ ...r, [queryKey]: { status: normStatus(report.status), report } }));
    } catch (e) {
      setRunError(`Re-sync failed for ${queryKey}: ${e?.message || 'error'}`);
    } finally {
      setResyncing((s) => ({ ...s, [queryKey]: false }));
    }
  };

  // Live tallies.
  const counts = useMemo(() => {
    const c = { ok: 0, warning: 0, error: 0, empty: 0, running: 0, unchecked: 0 };
    for (const k of keys) {
      const st = results[k.queryKey]?.status ?? 'unchecked';
      c[st] = (c[st] ?? 0) + 1;
    }
    return c;
  }, [keys, results]);

  const lastFlagged = useMemo(() => keys.filter((k) => k.hasError).length, [keys]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rank = { error: 0, warning: 1, running: 2, unchecked: 3, ok: 4, empty: 5 };
    return keys
      .filter((k) => !q || k.queryKey.toLowerCase().includes(q) || (k.name || '').toLowerCase().includes(q))
      .map((k) => ({ ...k, state: results[k.queryKey] }))
      .sort((a, b) => {
        const ra = rank[a.state?.status ?? 'unchecked'] ?? 3;
        const rb = rank[b.state?.status ?? 'unchecked'] ?? 3;
        if (ra !== rb) return ra - rb;
        return a.queryKey.localeCompare(b.queryKey);
      });
  }, [keys, results, search]);

  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4}>
            <Title order={2}>Query Health</Title>
            <Text c="dimmed" size="sm">
              Runs every stored query and checks it still returns the columns it&apos;s registered
              for. Each query is validated individually so you can watch progress; a red row is a
              query that will break a page, and Re-sync pulls its column list back in line with the
              SQL.
            </Text>
          </Stack>
          <Group gap="xs" wrap="nowrap">
            {running ? (
              <Button color="red" variant="light" leftSection={<IconPlayerStop size={16} />} onClick={cancel}>
                Cancel
              </Button>
            ) : (
              <Button
                leftSection={<IconPlayerPlay size={16} />}
                onClick={runAll}
                disabled={listLoading || !keys.length}
              >
                Validate all queries
              </Button>
            )}
          </Group>
        </Group>

        {listError && (
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />} title="Couldn't load query list">
            {String(listError?.message || listError)}
          </Alert>
        )}
        {runError && (
          <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />} title="Heads up">
            {runError}
          </Alert>
        )}

        {/* Progress / summary panel */}
        <Card withBorder radius="md" p="md">
          <Group align="center" gap="xl" wrap="nowrap">
            <RingProgress
              size={92}
              thickness={9}
              roundCaps
              label={
                <Text ta="center" size="sm" fw={700}>
                  {pct}%
                </Text>
              }
              sections={[
                { value: progress.total ? (counts.ok / progress.total) * 100 : 0, color: 'teal' },
                { value: progress.total ? (counts.warning / progress.total) * 100 : 0, color: 'yellow' },
                { value: progress.total ? (counts.error / progress.total) * 100 : 0, color: 'red' },
                { value: progress.total ? (counts.empty / progress.total) * 100 : 0, color: 'gray' },
              ]}
            />
            <Stack gap={6} style={{ flex: 1 }}>
              <Group gap="lg">
                <Text fw={600}>
                  {progress.done} / {progress.total || keys.length} checked
                </Text>
                <Badge color="teal" variant="light" size="lg">{counts.ok} OK</Badge>
                <Badge color="yellow" variant="light" size="lg">{counts.warning} warning</Badge>
                <Badge color="red" variant="light" size="lg">{counts.error} error</Badge>
                {counts.empty > 0 && (
                  <Badge color="gray" variant="light" size="lg">{counts.empty} empty stub</Badge>
                )}
                {running && (
                  <Group gap={6}>
                    <Loader size={14} />
                    <Text size="sm" c="dimmed">
                      {counts.running} running · ~{fmtEta(etaMs)} left
                    </Text>
                  </Group>
                )}
              </Group>
              <Progress.Root size="lg">
                <Progress.Section value={progress.total ? (counts.ok / progress.total) * 100 : 0} color="teal" />
                <Progress.Section value={progress.total ? (counts.warning / progress.total) * 100 : 0} color="yellow" />
                <Progress.Section value={progress.total ? (counts.error / progress.total) * 100 : 0} color="red" />
                <Progress.Section value={progress.total ? (counts.empty / progress.total) * 100 : 0} color="gray" />
              </Progress.Root>
              {!running && progress.done === 0 && (
                <Text size="xs" c="dimmed">
                  {listLoading
                    ? 'Loading query list…'
                    : `${keys.length} queries loaded. ${lastFlagged} were flagged on the last run. Click “Validate all queries” to re-check them live.`}
                </Text>
              )}
            </Stack>
          </Group>
        </Card>

        <Group justify="space-between">
          <TextInput
            placeholder="Filter by query key or name"
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            style={{ maxWidth: 360, flex: 1 }}
          />
          {!running && (
            <Button variant="subtle" size="sm" leftSection={<IconRefresh size={14} />} onClick={refetch}>
              Reload list
            </Button>
          )}
        </Group>

        <Card withBorder radius="md" p={0}>
          <div style={{ overflowX: 'auto' }}>
            <Table striped highlightOnHover verticalSpacing="xs" fz="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 110 }}>Status</Table.Th>
                  <Table.Th>Query</Table.Th>
                  <Table.Th>Problem</Table.Th>
                  <Table.Th ta="right" style={{ width: 70 }}>Rows</Table.Th>
                  <Table.Th style={{ width: 170 }} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((row) => {
                  const report = row.state?.report;
                  const drifted =
                    report?.missingStoredColumns?.length > 0 || report?.unmappedResultColumns?.length > 0;
                  return (
                    <Table.Tr key={row.queryKey}>
                      <Table.Td><StatusCell state={row.state} /></Table.Td>
                      <Table.Td>
                        <Group gap={6} wrap="nowrap">
                          {row.hasError && !row.state && (
                            <ThemeIcon size="xs" variant="light" color="red" title="Flagged on the last run">
                              <IconX size={10} />
                            </ThemeIcon>
                          )}
                          <div>
                            <Text ff="monospace" size="xs">{row.queryKey}</Text>
                            {row.name && <Text size="xs" c="dimmed">{row.name}</Text>}
                          </div>
                        </Group>
                      </Table.Td>
                      <Table.Td><Text size="xs">{problemText(report)}</Text></Table.Td>
                      <Table.Td ta="right">
                        <Text size="xs" c="dimmed">
                          {report?.executes ? report.rowCount?.toLocaleString() : '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs" justify="flex-end" wrap="nowrap">
                          {drifted && (
                            <Button
                              size="xs"
                              variant="light"
                              color="yellow"
                              loading={!!resyncing[row.queryKey]}
                              leftSection={<IconRefresh size={12} />}
                              onClick={() => resyncOne(row.queryKey)}
                            >
                              Re-sync
                            </Button>
                          )}
                          <Anchor
                            component={RouterLink}
                            to={`/query-commander/editor?queryKey=${encodeURIComponent(row.queryKey)}&admin=true`}
                            target="_blank"
                          >
                            <Button size="xs" variant="subtle" leftSection={<IconExternalLink size={12} />}>
                              Open
                            </Button>
                          </Anchor>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
                {!rows.length && (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Group justify="center" py="md">
                        <IconCheck size={16} />
                        <Text size="sm" c="dimmed">
                          {listLoading ? 'Loading…' : 'No queries match that filter.'}
                        </Text>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </div>
        </Card>
      </Stack>
    </Container>
  );
}
